import { Link, IconButton, TextField, Box, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState } from 'react';

interface HyperlinkFieldProps {
  label: string;
  value: string;
  onChange: (newValue: string) => void;
  placeholder?: string;
}

export const HyperlinkField: React.FC<HyperlinkFieldProps> = ({
  label,
  value,
  onChange,
  placeholder
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  // simple validation: must contain at least one dot and no spaces
  const isValid = draft.length === 0 || (/^[^\s]+\.[^\s]+$/).test(draft);

  const normalize = (raw: string) => {
    let url = raw.trim();
    if (!url) return '';
    if (!/^https?:\/\//i.test(url)) {
      url = 'http://' + url;
    }
    return url;
  };

  const handleSave = () => {
    if (!isValid) return;
    const normalized = normalize(draft);
    onChange(normalized);
    setEditing(false);
  };

  const handleCancel = () => {
    setEditing(false);
    setDraft(value);
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {!editing ? (
        <>
          <Link
            href={value || undefined}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ 
              color: '#6a5acd',
              overflowWrap: 'break-word', 
              wordBreak: 'break-all',     
              display: 'inline-block',    
              maxWidth: '100%',           
            }}
          >
            {value || placeholder || ''}
          </Link>
          <Tooltip title={`Edit ${label}`}>
            <IconButton size="small" onClick={() => setEditing(true)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <>
          <TextField
            size="small"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            error={!isValid}
            helperText={!isValid ? 'Enter a valid domain or URL' : ''}
            sx={{ maxWidth: 300 }}
          />
          <Tooltip title="Save">
            <span>
              <IconButton size="small" onClick={handleSave} disabled={!isValid}>
                <CheckIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton size="small" onClick={handleCancel}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
};
